import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import CommercialLayout from '../../components/commercial/CommercialLayout';
import { useAuth } from '../../contexts/AuthContext';
import pb from '../../lib/pocketbase';
import SignatureCanvas from 'react-signature-canvas';
import confetti from 'canvas-confetti';

export default function CommercialAdminPage() {
    const { user } = useAuth();
    const navigate = useNavigate();
    const sigPad = useRef({});

    // Steps: 0: Info/Docs, 1: Contract Review, 2: Signature, 3: Success
    const [step, setStep] = useState(0);
    const [loading, setLoading] = useState(false);
    const [errorDetails, setErrorDetails] = useState(null);

    // Form Data
    const [formData, setFormData] = useState({
        legalStatus: 'auto_entrepreneur',
        siret: '',
        iban: '',
        bic: '',
        idCardFront: null,
        idCardBack: null,
    });

    const [filesNames, setFilesNames] = useState({
        idCardFront: '',
        idCardBack: ''
    });

    // Validations
    const canProceedToContract = formData.siret && formData.iban && formData.idCardFront;

    // Load existing data
    useEffect(() => {
        async function loadData() {
            try {
                const record = await pb.collection('commercial_profiles').getFirstListItem(`user="${user.id}"`);
                if (record) {
                    setFormData(prev => ({
                        ...prev,
                        legalStatus: record.legal_status || 'auto_entrepreneur',
                        siret: record.siret || '',
                        iban: record.iban || '',
                    }));
                    // Note: We don't pre-fill file inputs as they are read-only for security in browsers usually
                    if (record.idCardFront) setFilesNames(prev => ({ ...prev, idCardFront: 'Document existant' }));
                    if (record.idCardBack) setFilesNames(prev => ({ ...prev, idCardBack: 'Document existant' }));
                }
            } catch (err) {
                console.log("No existing profile data found.");
            }
        }
        loadData();
    }, [user.id]);

    const handleFileChange = (e, field) => {
        const file = e.target.files[0];
        if (file) {
            setFormData(prev => ({ ...prev, [field]: file }));
            setFilesNames(prev => ({ ...prev, [field]: file.name }));
        }
    };

    const handleInput = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const clearSignature = () => {
        sigPad.current.clear();
    };

    const handleFinalize = async () => {
        if (sigPad.current.isEmpty()) {
            alert("Veuillez signer le contrat pour continuer.");
            return;
        }

        setLoading(true);
        try {
            // Use Vector Data (JSON) instead of Base64 Image to allow storage in standard Text field without errors
            const signatureData = JSON.stringify(sigPad.current.toData());

            // Logic to save all this data to Pocketbase 'commercial_profiles'
            // Ideally we would upload files first, then update record.

            const record = await pb.collection('commercial_profiles').getFirstListItem(`user="${user.id}"`);

            // Save all Admin Data to Pocketbase
            await pb.collection('commercial_profiles').update(record.id, {
                status: 'active',
                level: 3,
                legal_status: formData.legalStatus,
                siret: formData.siret,
                iban: formData.iban,
                signature: signatureData
            });

            confetti({
                particleCount: 300,
                spread: 120,
                origin: { y: 0.6 },
                colors: ['#22c55e', '#ffffff', '#fbbf24']
            });

            // setStep(3); // Success Screen
            // Redirect after delay
            setTimeout(() => {
                navigate('/commercial');
            }, 2500);

        } catch (err) {
            console.error("Error signing contract:", err);
            let msg = "Erreur lors de la signature.";
            let details = "";

            // Pocketbase detailed validation errors
            if (err.response && err.response.data) {
                const errors = err.response.data; // This is usually { code, message, data: { field: ... }}
                if (errors.data) {
                    details = Object.entries(errors.data).map(([key, val]) => `${key}: ${val.message}`).join(', ');
                } else {
                    details = errors.message || "Erreur inconnue";
                }
            } else if (err.data) {
                if (err.data.data) {
                    details = Object.entries(err.data.data).map(([key, val]) => `${key}: ${val.message}`).join(', ');
                } else {
                    details = err.data.message;
                }
            } else {
                details = err.message;
            }

            setErrorDetails(msg + " " + details);
            alert(msg + "\n" + details);
        } finally {
            setLoading(false);
        }
    };

    return (
        <CommercialLayout title="Administration & Contrat">
            <div style={{ maxWidth: 900, margin: '0 auto', padding: '40px 20px', fontFamily: 'Inter, sans-serif' }}>

                {/* HEADER */}
                <div style={{ textAlign: 'center', marginBottom: 40 }}>
                    <div style={{ fontSize: '3rem', marginBottom: 10 }}>✍️</div>
                    <h1 style={{ fontSize: '2rem', fontWeight: 800, margin: 0, color: 'var(--commercial-text)' }}>
                        {step === 0 && "Informations Administratives"}
                        {step === 1 && "Revue du Contrat Partenaire"}
                        {step === 2 && "Signature Officielle"}
                    </h1>
                    <p style={{ color: 'var(--commercial-muted)', marginTop: 10 }}>
                        Étape {step + 1} sur 3 • Finalisation du dossier
                    </p>
                </div>

                {/* STEP 0: DOCUMENTS & INFO */}
                {step === 0 && (
                    <div style={{ background: 'var(--commercial-panel)', padding: 30, borderRadius: 20, border: '1px solid var(--commercial-border)', boxShadow: '0 10px 40px -10px rgba(0,0,0,0.1)' }}>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 30 }}>
                            {/* Legal Info */}
                            <div>
                                <h3 style={{ marginTop: 0, display: 'flex', alignItems: 'center', gap: 10 }}>
                                    <span>🏢</span> Statut & Banque
                                </h3>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 15 }}>
                                    <div>
                                        <label style={{ display: 'block', fontSize: '0.9rem', fontWeight: 600, marginBottom: 5 }}>Statut Juridique</label>
                                        <select
                                            name="legalStatus"
                                            value={formData.legalStatus}
                                            onChange={handleInput}
                                            style={{ width: '100%', padding: 12, borderRadius: 8, border: '1px solid var(--commercial-border)', background: 'var(--commercial-panel-2)', color: 'var(--commercial-text)' }}
                                        >
                                            <option value="auto_entrepreneur">Auto-Entrepreneur</option>
                                            <option value="ei">Entreprise Individuelle (EI)</option>
                                            <option value="sas_sasu">SAS / SASU</option>
                                            <option value="sarl_eurl">SARL / EURL</option>
                                        </select>
                                    </div>
                                    <div>
                                        <label style={{ display: 'block', fontSize: '0.9rem', fontWeight: 600, marginBottom: 5 }}>Numéro SIRET</label>
                                        <input
                                            type="text"
                                            name="siret"
                                            value={formData.siret}
                                            onChange={handleInput}
                                            placeholder="123 456 789 00012"
                                            style={{ width: '100%', padding: 12, borderRadius: 8, border: '1px solid var(--commercial-border)', background: 'var(--commercial-panel-2)', color: 'var(--commercial-text)' }}
                                        />
                                    </div>
                                    <div>
                                        <label style={{ display: 'block', fontSize: '0.9rem', fontWeight: 600, marginBottom: 5 }}>IBAN (Pour Commissions)</label>
                                        <input
                                            type="text"
                                            name="iban"
                                            value={formData.iban}
                                            onChange={handleInput}
                                            placeholder="FR76 ...."
                                            style={{ width: '100%', padding: 12, borderRadius: 8, border: '1px solid var(--commercial-border)', background: 'var(--commercial-panel-2)', color: 'var(--commercial-text)' }}
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* Documents */}
                            <div>
                                <h3 style={{ marginTop: 0, display: 'flex', alignItems: 'center', gap: 10 }}>
                                    <span>📂</span> Pièces Justificatives
                                </h3>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 15 }}>
                                    <div style={{ padding: 20, border: '2px dashed var(--commercial-border)', borderRadius: 12, textAlign: 'center', background: 'var(--commercial-panel-2)' }}>
                                        <div style={{ fontSize: '2rem', marginBottom: 10 }}>🆔</div>
                                        <div style={{ fontWeight: 600, marginBottom: 5 }}>Pièce d'Identité (Recto)</div>
                                        {filesNames.idCardFront ? (
                                            <div style={{ color: '#22c55e', fontSize: '0.9rem' }}>✅ {filesNames.idCardFront}</div>
                                        ) : (
                                            <>
                                                <label htmlFor="id_front" style={{ display: 'inline-block', padding: '8px 16px', background: 'var(--commercial-panel)', border: '1px solid var(--commercial-border)', borderRadius: 6, cursor: 'pointer', fontSize: '0.9rem', marginTop: 10 }}>Parcourir</label>
                                                <input type="file" id="id_front" hidden onChange={(e) => handleFileChange(e, 'idCardFront')} />
                                            </>
                                        )}
                                    </div>
                                    <div style={{ padding: 20, border: '2px dashed var(--commercial-border)', borderRadius: 12, textAlign: 'center', background: 'var(--commercial-panel-2)' }}>
                                        <div style={{ fontSize: '2rem', marginBottom: 10 }}>🏦</div>
                                        <div style={{ fontWeight: 600, marginBottom: 5 }}>Relevé d'Identité Bancaire (RIB)</div>
                                        {filesNames.idCardBack ? ( // Reusing UI logic, renaming logically would be better but keeping simple
                                            <div style={{ color: '#22c55e', fontSize: '0.9rem' }}>✅ {filesNames.idCardBack}</div>
                                        ) : (
                                            <>
                                                <label htmlFor="rib_file" style={{ display: 'inline-block', padding: '8px 16px', background: 'var(--commercial-panel)', border: '1px solid var(--commercial-border)', borderRadius: 6, cursor: 'pointer', fontSize: '0.9rem', marginTop: 10 }}>Parcourir</label>
                                                <input type="file" id="rib_file" hidden onChange={(e) => handleFileChange(e, 'idCardBack')} />
                                            </>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div style={{ marginTop: 40, textAlign: 'right' }}>
                            <button
                                onClick={() => setStep(1)}
                                disabled={!canProceedToContract}
                                style={{
                                    padding: '16px 32px',
                                    background: canProceedToContract ? 'var(--commercial-accent)' : 'var(--commercial-muted)',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: 12,
                                    fontSize: '1rem',
                                    fontWeight: 700,
                                    cursor: canProceedToContract ? 'pointer' : 'not-allowed',
                                    opacity: canProceedToContract ? 1 : 0.5
                                }}
                            >
                                Suivant : Contrat Partenaire &rarr;
                            </button>
                        </div>
                    </div>
                )}

                {/* STEP 1: READ CONTRACT */}
                {step === 1 && (
                    <div style={{ background: 'var(--commercial-panel)', padding: 0, borderRadius: 20, border: '1px solid var(--commercial-border)', overflow: 'hidden', boxShadow: '0 10px 40px -10px rgba(0,0,0,0.1)' }}>
                        <div style={{ padding: 20, borderBottom: '1px solid var(--commercial-border)', background: 'var(--commercial-panel-2)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <h3 style={{ margin: 0 }}>📜 Contrat de Partenariat Commercial</h3>
                            <button onClick={() => window.print()} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '1.2rem' }}>🖨️</button>
                        </div>

                        <div style={{ height: '500px', overflowY: 'auto', padding: 40, fontFamily: 'Times New Roman, serif', lineHeight: 1.6, background: '#fff', color: '#000' }}>
                            <h2 style={{ textAlign: 'center', textDecoration: 'underline' }}>CONTRAT D'APPORTEUR D'AFFAIRES</h2>
                            <p><strong>Entre les soussignés :</strong></p>
                            <p>La société INFOCOLAB, ci-après dénommée "L'Entreprise",</p>
                            <p>ET</p>
                            <p>M/Mme <strong>{user?.name || 'Le Commercial'}</strong>, ci-après dénommé "Le Partenaire".</p>

                            <h3>ARTICLE 1 : OBJET</h3>
                            <p>Le présent contrat a pour objet de définir les conditions dans lesquelles le Partenaire présentera des clients potentiels à l'Entreprise...</p>

                            <h3>ARTICLE 2 : COMMISSIONS</h3>
                            <p>Le Partenaire percevra une commission sur le chiffre d'affaires généré par les clients apportés, selon la grille tarifaire en vigueur...</p>

                            <h3>ARTICLE 3 : OBLIGATIONS</h3>
                            <p>Le Partenaire s'engage à respecter la charte éthique et à ne pas concurrencer déloyalement l'Entreprise...</p>

                            <p style={{ marginTop: 50, fontStyle: 'italic' }}>[... Suite des clauses juridiques standards ...]</p>

                            <br /><br /><br />
                        </div>

                        <div style={{ padding: 20, borderTop: '1px solid var(--commercial-border)', textAlign: 'right', background: 'var(--commercial-panel-2)' }}>
                            <button
                                onClick={() => setStep(0)}
                                style={{ padding: '12px 24px', marginRight: 10, background: 'transparent', border: '1px solid var(--commercial-border)', borderRadius: 8, cursor: 'pointer', color: 'var(--commercial-text)' }}
                            >
                                &larr; Retour
                            </button>
                            <button
                                onClick={() => setStep(2)}
                                style={{ padding: '12px 24px', background: 'var(--commercial-accent)', color: 'white', border: 'none', borderRadius: 8, fontWeight: 700, cursor: 'pointer' }}
                            >
                                Lu et Approuvé : Passer à la Signature &rarr;
                            </button>
                        </div>
                    </div>
                )}


                {/* STEP 2: SIGNATURE */}
                {step === 2 && (
                    <div style={{ textAlign: 'center' }}>
                        <div style={{ background: 'var(--commercial-panel)', padding: 40, borderRadius: 24, border: '1px solid var(--commercial-border)', boxShadow: '0 20px 50px -20px rgba(0,0,0,0.2)', display: 'inline-block', maxWidth: '100%' }}>
                            <h2 style={{ marginTop: 0, marginBottom: 10 }}>Signature Officielle</h2>
                            <p style={{ color: 'var(--commercial-muted)', marginBottom: 30 }}>
                                En signant ci-dessous, vous acceptez les termes du contrat de partenariat.
                            </p>

                            <div style={{ border: '2px solid var(--commercial-border)', borderRadius: 12, overflow: 'hidden', display: 'inline-block', background: '#fff', position: 'relative' }}>
                                <SignatureCanvas
                                    penColor="black"
                                    canvasProps={{ width: 500, height: 200, className: 'sigCanvas' }}
                                    ref={sigPad}
                                />
                                <div style={{ position: 'absolute', bottom: 10, left: 10, fontSize: '0.8rem', opacity: 0.4, pointerEvents: 'none' }}>Signez ici avec votre souris ou doigt</div>
                            </div>

                            <div style={{ marginTop: 20, display: 'flex', justifyContent: 'center', gap: 15 }}>
                                <button
                                    onClick={clearSignature}
                                    style={{ padding: '12px 24px', background: '#fee2e2', color: '#ef4444', border: 'none', borderRadius: 8, fontWeight: 600, cursor: 'pointer' }}
                                >
                                    Effacer
                                </button>
                                <button
                                    onClick={handleFinalize}
                                    disabled={loading}
                                    style={{
                                        padding: '12px 32px',
                                        background: '#22c55e',
                                        color: 'white',
                                        border: 'none',
                                        borderRadius: 8,
                                        fontWeight: 700,
                                        cursor: loading ? 'wait' : 'pointer',
                                        boxShadow: '0 10px 20px -5px rgba(34, 197, 94, 0.4)'
                                    }}
                                >
                                    {loading ? 'Validation...' : 'VALIDER & SIGNER'}
                                </button>
                            </div>
                            {errorDetails && (
                                <div style={{ marginTop: 20, color: '#ef4444', background: '#fee2e2', padding: 15, borderRadius: 8, textAlign: 'center', fontWeight: 'bold' }}>
                                    {errorDetails}
                                </div>
                            )}
                        </div>
                    </div>
                )}

            </div>
        </CommercialLayout>
    );
}
